import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Icon } from '@pella/sash-framework';
import { useGlobalConfig } from '../../../hooks/use-global-config';
import { getCalendar } from '../../../actions/rta-actions';
import { isValidArray } from '../../utilities/validations';
import { CTA, LoadingOverlay } from '../../';

const AvailableAppointments = ({ zip, className }) => {
	const { rta_cards } = useGlobalConfig();
	const dispatch = useDispatch();
	const rtaData = useSelector((state) => state.rtaData);
	const [availableDays, setAvailableDays] = useState(0);
	const [error, setError] = useState(null);
	const [status, setStatus] = useState('idle');

	useEffect(() => {
		setStatus('loading');
		dispatch(getCalendar('Web', zip));
	}, [zip, dispatch]);

	useEffect(() => {
		setError(null);

		if (rtaData?.Timeslots?.length > 0) {
			getAvailableWeek();
			setStatus('success');
		} else if (rtaData?.status === 'failed') {
			setError(rtaData?.errorMessage);
			setStatus('error');
		} else {
			setError('No available appointments in your area.');
			setStatus('error');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only check for time slot changes
	}, [rtaData]);

	const getAvailableWeek = () => {
		const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
		const weekAhead = new Date(Date.now() + 24 * 60 * 60 * 1000 * 8);

		const availableDays = rtaData.Timeslots.filter((slot) => slot.START_DATE >= tomorrow.toISOString() && slot.START_DATE <= weekAhead.toISOString());
		setAvailableDays(availableDays.length);
	};

	return (
		<div
			className={`availableAppts relative flex flex-col border border-neutral-200 bg-white p-4 md:p-6 xl:p-10 ${className || ''}`}
			data-testid='available-appointments'
		>
			{status === 'loading' && (
				<LoadingOverlay
					fixed={false}
					svgSize={200}
				/>
			)}

			{rta_cards?.card?.[0]?.heading && (
				<h3 className='availableAppts-heading mb-0 flex flex-row items-center text-lg md:mb-6 xl:mb-5 xl:text-xl'>
					{rta_cards.card[0].material_symbol && (
						<Icon
							name={rta_cards.card[0].material_symbol}
							className='availableAppts-icon mr-3 block flex-none md:mr-4 xl:mr-2'
							size={32}
						/>
					)}

					<span>{rta_cards.card[0].heading}</span>
				</h3>
			)}

			{status === 'error' && error && (
				<Alert
					type='error'
					className='availableAppts-alert mt-4 md:my-auto'
					hideClose
				>
					{error}
				</Alert>
			)}

			{status === 'success' && (
				<div className='availableAppts-success my-auto grid grid-cols-[auto_1fr] items-center gap-x-4 py-8 xl:px-6'>
					<span className='block text-[56px] font-bold !leading-none'>{availableDays}</span>
					{rta_cards?.card?.[0]?.ho_text && <p className='max-w-[30ch] text-neutral'>{rta_cards.card[0].ho_text}</p>}
				</div>
			)}

			{availableDays > 0 && !error && isValidArray(rta_cards?.card?.[0]?.cta) && (
				<div className='mt-auto xl:px-6'>
					<CTA
						className='availableAppts-cta'
						data={rta_cards.card[0].cta[0]}
						size='sm'
						wide
					/>
				</div>
			)}
		</div>
	);
};

AvailableAppointments.propTypes = {
	zip: PropTypes.string,
	className: PropTypes.string,
};

export default AvailableAppointments;
